import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  listEnrolments,
  downloadEnrolments,
  downloadEnrolmentsTemplate,
  deleteEnrollments,
  toggleEnrolmentActiveStatus,
  type EnrolmentItem,
} from "../../../api/academyEnrolments/enrolmentsService";
import { useDebounce } from "../../../hooks/useDebounce";
import configService from "../../../services/configService";

export type DateRange = { startDate: string; endDate: string };

export interface SportOption {
  value: string;
  label: string;
}

export function useAcademyEnrolments() {
  const navigate = useNavigate();

  const [enrolments, setEnrolments] = useState<EnrolmentItem[]>([]);
  const [sportOptions, setSportOptions] = useState<SportOption[]>([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [enrolledThroughFilter, setEnrolledThroughFilter] = useState("");
  const [sportFilter, setSportFilter] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: "", endDate: "" });
  const [paidAt, setPaidAt] = useState("");

  // ── Selection & delete state ─────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<{ ids: string[] } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ── Toggle active state ───────────────────────────────────────────────────────
  const [toggleTarget, setToggleTarget] = useState<EnrolmentItem | null>(null);
  const [isToggling, setIsToggling] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 600);

  useEffect(() => {
    configService.getConfig().then((config) => {
      const options: SportOption[] = config.sports
        .filter((s) => s.isActive)
        .map((s) => ({ value: s.name.replace(/\s+/g, ""), label: s.name }));
      setSportOptions(options);
    });
  }, []);

  const handleAddEnrolment = useCallback(() => {
    navigate("/academy-enrolments/add");
  }, [navigate]);

  const handleViewEnrolment = useCallback(
    (enrolment: EnrolmentItem) => {
      navigate(`/academy-enrolments/${enrolment._id}`);
    },
    [navigate]
  );

  const handleEditEnrolment = useCallback(
    (enrolment: EnrolmentItem) => {
      navigate(`/academy-enrolments/${enrolment._id}`, {
        state: { startInEdit: true },
      });
    },
    [navigate]
  );

  const handleOpenUploadModal = useCallback(() => {
    setUploadModalOpen(true);
  }, []);

  const handleCloseUploadModal = useCallback(() => {
    setUploadModalOpen(false);
  }, []);

  const fetchEnrolments = useCallback(async () => {
    setIsPageLoading(true);
    setError(null);

    try {
      const result = await listEnrolments({
        pageNum: page,
        pageSize,
        search: debouncedSearch.trim() || undefined,
        enrolledThrough: enrolledThroughFilter || undefined,
        sports: sportFilter || undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        paidAt: paidAt || undefined,
      });

      setEnrolments(result.data.paginatedResults);
      setTotalCount(result.data.totalCount);
      setTotalPages(Math.max(1, Math.ceil(result.data.totalCount / pageSize)));
    } catch (err: unknown) {
      const anyErr = err as any;
      const responseData = anyErr?.response?.data;
      const errorField = responseData?.error;
      const apiMessage: string =
        (typeof errorField === "string"
          ? errorField
          : typeof errorField?.message === "string"
          ? errorField.message
          : null) ||
        (typeof responseData?.message === "string"
          ? responseData.message
          : null) ||
        "Failed to fetch enrolments";
      console.error("Failed to fetch enrolments:", err);
      setError(apiMessage);
      setEnrolments([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setIsPageLoading(false);
    }
  }, [page, pageSize, debouncedSearch, enrolledThroughFilter, sportFilter, dateRange, paidAt]);

  const handleDownload = useCallback(async () => {
    try {
      const blob = await downloadEnrolments({
        search: debouncedSearch.trim() || undefined,
        enrolledThrough: enrolledThroughFilter || undefined,
        sports: sportFilter || undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "enrolments.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download enrolments:", err);
    }
  }, [debouncedSearch, enrolledThroughFilter, sportFilter, dateRange]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const blob = await downloadEnrolmentsTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "enrolments_template.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download template:", err);
    }
  }, []);

  // ── Selection helpers ────────────────────────────────────────────────────────

  const isDeletable = useCallback((enrolment: EnrolmentItem): boolean => {
    const lower = (enrolment.enrolledThrough ?? "").trim().toLowerCase();
    return lower === "excel" || lower === "offline" || lower === "partner app - offline";
  }, []);

  const toggleSelectOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleSelectAllOnPage = useCallback(
    (rows: EnrolmentItem[]) => {
      const deletableOnPage = rows.filter(isDeletable).map((e) => e._id);
      setSelectedIds((prev) => {
        const allSelected = deletableOnPage.every((id) => prev.has(id));
        const next = new Set(prev);
        if (allSelected) {
          deletableOnPage.forEach((id) => next.delete(id));
        } else {
          deletableOnPage.forEach((id) => next.add(id));
        }
        return next;
      });
    },
    [isDeletable]
  );

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // ── Delete handlers ──────────────────────────────────────────────────────────

  const handleRequestDeleteSingle = useCallback((enrolment: EnrolmentItem) => {
    setDeleteError(null);
    setDeleteTarget({ ids: [enrolment._id] });
  }, []);

  const handleRequestDeleteBulk = useCallback(() => {
    setDeleteError(null);
    setSelectedIds((prev) => {
      setDeleteTarget({ ids: Array.from(prev) });
      return prev;
    });
  }, []);

  const handleCancelDelete = useCallback(() => {
    setDeleteTarget(null);
    setDeleteError(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteEnrollments(deleteTarget.ids);
      const deletedSet = new Set(deleteTarget.ids);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        deletedSet.forEach((id) => next.delete(id));
        return next;
      });
      setDeleteTarget(null);
      toast.success(
        deleteTarget.ids.length === 1
          ? "Enrolment deleted successfully."
          : `${deleteTarget.ids.length} enrolments deleted successfully.`
      );
      await fetchEnrolments();
    } catch (err: unknown) {
      const anyErr = err as any;
      const apiMessage: string =
        anyErr?.response?.data?.error?.message ||
        anyErr?.response?.data?.error ||
        anyErr?.response?.data?.message ||
        "Failed to delete enrolment(s). Please try again.";
      setDeleteError(typeof apiMessage === "string" ? apiMessage : "Failed to delete enrolment(s).");
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, fetchEnrolments]);

  // ── Toggle active handlers ────────────────────────────────────────────────────

  const handleRequestToggleActive = useCallback((enrolment: EnrolmentItem) => {
    setToggleError(null);
    setToggleTarget(enrolment);
  }, []);

  const handleCancelToggle = useCallback(() => {
    setToggleTarget(null);
    setToggleError(null);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!toggleTarget) return;
    setIsToggling(true);
    setToggleError(null);
    try {
      const updated = await toggleEnrolmentActiveStatus(toggleTarget._id);
      setEnrolments((prev) =>
        prev.map((row) =>
          row._id === updated._id ? { ...row, isActive: updated.isActive } : row
        )
      );
      setToggleTarget(null);
      toast.success(
        updated.isActive
          ? "Enrolment marked active successfully."
          : "Enrolment marked inactive successfully."
      );
    } catch (err: unknown) {
      const anyErr = err as any;
      const apiMessage: string =
        anyErr?.response?.data?.error?.message ||
        anyErr?.response?.data?.error ||
        anyErr?.response?.data?.message ||
        "Failed to update enrolment status. Please try again.";
      setToggleError(typeof apiMessage === "string" ? apiMessage : "Failed to update enrolment status.");
    } finally {
      setIsToggling(false);
    }
  }, [toggleTarget]);

  // Reset to first page when search/filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, enrolledThroughFilter, sportFilter, dateRange, paidAt]);

  useEffect(() => {
    fetchEnrolments();
  }, [fetchEnrolments]);

  return {
    functions: {
      handleAddEnrolment,
      handleViewEnrolment,
      handleEditEnrolment,
      handleOpenUploadModal,
      handleCloseUploadModal,
      handleDownload,
      handleDownloadTemplate,
      refetchEnrolments: fetchEnrolments,
      isDeletable,
      toggleSelectOne,
      toggleSelectAllOnPage,
      clearSelection,
      handleRequestDeleteSingle,
      handleRequestDeleteBulk,
      handleCancelDelete,
      handleConfirmDelete,
      handleRequestToggleActive,
      handleCancelToggle,
      handleConfirmToggle,
    },
    values: {
      enrolments,
      sportOptions,
      uploadModalOpen,
      page,
      pageSize,
      totalCount,
      totalPages,
      search,
      enrolledThroughFilter,
      sportFilter,
      dateRange,
      paidAt,
      selectedIds,
      deleteTarget,
      isDeleting,
      deleteError,
      toggleTarget,
      isToggling,
      toggleError,
    },
    loading: {
      isPageLoading,
    },
    error,
    setters: {
      setPage,
      setPageSize,
      setSearch,
      setEnrolledThroughFilter,
      setSportFilter,
      setDateRange,
      setPaidAt,
    },
  };
}
